import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { SessionMentor } from '../../../types/mentor';
import { PiPSessionWindow } from '../components/PictureInPicture';
import { DataChannelMessage } from './useDataChannel';

// Type declarations for Document Picture-in-Picture API
interface DocumentPictureInPictureAPI {
  requestWindow(options?: {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
  }): Promise<Window>;
  window: Window | null;
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPictureAPI;
  }
}

export interface PiPMessage {
  type: string;
  data: {
    isAvatarTalking?: boolean;
    isFileUploading?: boolean;
    shareScreenState?: boolean;
  };
}

interface PiPSyncState {
  // Audio state
  volume: number;
  isMuted: boolean;
  // shareScreenState
  isScreenSharing: boolean;

  // Microphone state
  isMicMuted: boolean;

  // Avatar state
  avatarReady: boolean;

  // Session state
  companionName: string;
  avatarUrl?: string;

  // Upload state
  isFileUploading: boolean;

  // Reminder state (for future use)
  reminderVisible?: boolean;
  reminderMessage?: string;
}

interface UseSessionPiPProps {
  videoElement: HTMLVideoElement | null;
  isSessionActive: boolean;
  hasMediaStream?: boolean;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  initialVolume?: number;
  initialMuted?: boolean;
  companionData?: SessionMentor | null;
  // Current state for sync
  avatarReady?: boolean;
  // Callbacks
  onVolumeChange?: (volume: number) => void;
  onMuteChange?: (muted: boolean) => void;
  onMicMuteChange?: (micMuted: boolean) => void;
  onPiPEnter?: () => void;
  onPiPExit?: (finalState: Partial<PiPSyncState>) => void;
  onEndSessionComplete?: () => void;
  isMuted?: boolean;
  muteMicrophone?: (muted: boolean) => void;
  userMicrophoneStream?: MediaStream | null;
  // Screen sharing
  startScreenShare?: () => Promise<void>;
  stopScreenShare?: () => void;
  isScreenSharing: boolean;

  // Upload controls
  avatarId?: string;
  organizationId?: string;
  // Upload state
  isFileUploading?: boolean;
  socketId?: string;

  // Stop talking controls
  onStopTalking?: () => void;
  sessionId?: string;
  sendMessage?: (message: DataChannelMessage) => void;
  onTrackUploadEvent?: (event: string) => void;
  hideScreenShareButton: boolean;
  isWatchSession: boolean;
}

interface UseSessionPiPReturn {
  isPiPActive: boolean;
  isPiPSupported: boolean;
  enterPiP: () => Promise<void>;
  exitPiP: () => void;
  togglePiP: () => Promise<void>;
  sendMessageToPiP: (message: PiPMessage) => void;
}

export const useSessionPiP = ({
  videoElement,
  isSessionActive,
  hasMediaStream = false,
  audioRef,
  initialVolume = 1,
  initialMuted = false,
  companionData,
  avatarReady = false,
  onVolumeChange,
  onMuteChange,
  onMicMuteChange,
  onPiPEnter,
  onPiPExit,
  onEndSessionComplete,
  isMuted,
  muteMicrophone,
  userMicrophoneStream,
  isScreenSharing = false,
  startScreenShare,
  stopScreenShare,
  avatarId,
  organizationId,
  isFileUploading = false,
  onStopTalking,
  sessionId,
  sendMessage,
  onTrackUploadEvent,
  hideScreenShareButton,
  isWatchSession,
  socketId,
}: UseSessionPiPProps): UseSessionPiPReturn => {
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const isAutoEnteringPiP = useRef(false);
  const pipWindowRef = useRef<Window | null>(null);
  const reactRootRef = useRef<ReturnType<typeof createRoot> | null>(null);

  // Send real-time updates to PiP window
  const sendMessageToPiP = useCallback((message: PiPMessage) => {
    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.postMessage(message, '*');
    }
  }, []);

  // Collect current state for PiP
  const getCurrentState = useCallback((): PiPSyncState => {
    const currentVolume = audioRef?.current?.volume ?? initialVolume;
    const currentMuted = audioRef?.current?.muted ?? initialMuted;
    const isMicMuted = userMicrophoneStream
      ? userMicrophoneStream.getAudioTracks().some((track) => !track.enabled)
      : false;

    return {
      volume: currentVolume,
      isMuted: currentMuted,
      isMicMuted,
      avatarReady,
      companionName: companionData
        ? `${companionData.firstName} ${companionData.lastName}`
        : '',
      avatarUrl: companionData?.avatar?.videos?.[0]?.preview_url,
      isFileUploading,
      isScreenSharing,
    };
  }, [
    audioRef,
    initialVolume,
    initialMuted,
    avatarReady,
    companionData,
    userMicrophoneStream,
    isFileUploading,
    isScreenSharing,
  ]);

  // Apply state changes from PiP
  const applyStateChanges = useCallback(
    (finalState: Partial<PiPSyncState>) => {
      if (audioRef?.current && finalState.volume !== undefined) {
        audioRef.current.volume = finalState.volume;
        onVolumeChange?.(finalState.volume);
      }

      if (audioRef?.current && finalState.isMuted !== undefined) {
        audioRef.current.muted = finalState.isMuted;
        onMuteChange?.(finalState.isMuted);
      }

      if (finalState.isMicMuted !== undefined) {
        if (muteMicrophone) {
          muteMicrophone(finalState.isMicMuted);
        }
        onMicMuteChange?.(finalState.isMicMuted);
      }

      // Sync upload states back to main page
    },
    [
      audioRef,
      onVolumeChange,
      onMuteChange,
      onMicMuteChange,
      muteMicrophone,
      isFileUploading,
    ],
  );

  // Check if Document PiP is supported
  useEffect(() => {
    const checkPiPSupport = () => {
      const supported = Boolean(
        'documentPictureInPicture' in window &&
          window.documentPictureInPicture &&
          typeof window.documentPictureInPicture === 'object' &&
          'requestWindow' in window.documentPictureInPicture,
      );

      setIsPiPSupported(supported);
    };

    checkPiPSupport();
  }, []);

  // Handle PiP events
  useEffect(() => {
    if (!videoElement || !isPiPSupported) return;

    const handleEnterPiP = () => {
      setIsPiPActive(true);
      onPiPEnter?.();
    };

    const handleExitPiP = () => {
      setIsPiPActive(false);
      isAutoEnteringPiP.current = false;
      // Pass empty state since this is just video PiP exit, not our custom PiP
      onPiPExit?.({});
    };

    videoElement.addEventListener('enterpictureinpicture', handleEnterPiP);
    videoElement.addEventListener('leavepictureinpicture', handleExitPiP);

    return () => {
      videoElement.removeEventListener('enterpictureinpicture', handleEnterPiP);
      videoElement.removeEventListener('leavepictureinpicture', handleExitPiP);
    };
  }, [videoElement, isPiPSupported, onPiPEnter, onPiPExit]);

  const createPiPContent = useCallback(
    (pipWindow: Window, currentVideoElement: HTMLVideoElement | null) => {
      const companionName = companionData
        ? `${companionData.firstName} ${companionData.lastName}`
        : 'Session';

      // Set document title
      pipWindow.document.title = `${companionName} - Session`;

      // Copy stylesheets from main window
      const copyStyleSheets = () => {
        [...document.styleSheets].forEach((styleSheet) => {
          try {
            const cssRules = [...styleSheet.cssRules]
              .map((rule) => rule.cssText)
              .join('');
            const style = pipWindow.document.createElement('style');
            style.textContent = cssRules;
            pipWindow.document.head.appendChild(style);
          } catch {
            const link = pipWindow.document.createElement('link');
            link.rel = 'stylesheet';
            link.type = styleSheet.type || 'text/css';
            link.media = styleSheet.media.mediaText;
            link.href = styleSheet.href || '';
            pipWindow.document.head.appendChild(link);
          }
        });
      };

      // Create React root and render component
      const renderReactComponent = () => {
        // Create a container div for React
        const container = pipWindow.document.createElement('div');
        container.id = 'pip-react-root';
        pipWindow.document.body.appendChild(container);

        // Create React root
        const root = createRoot(container);
        reactRootRef.current = root;

        // Get current state for sync
        const currentState = getCurrentState();
        console.log('currentState', currentState);

        // Render the PiP component
        root.render(
          React.createElement(PiPSessionWindow, {
            isWatchSession,
            isMuted,
            pipWindow,
            videoElement: currentVideoElement,
            audioRef,
            initialVolume: currentState.volume,
            initialMuted: currentState.isMuted,
            initialIsScreenSharing: currentState.isScreenSharing,
            onVolumeChange,
            onMuteChange,
            onEndSessionComplete,
            muteMicrophone,
            startScreenShare,
            stopScreenShare,
            // Upload props
            isAuthenticated: true,
            avatarId,
            organizationId,
            // Upload state sync
            initialIsFileUploading: currentState.isFileUploading,
            // Stop talking props
            onStopTalking,
            sessionId,
            socketId,
            sendMessage,
            onTrackUploadEvent,
            hideScreenShareButton,
          }),
        );
      };

      // Build the PiP window content
      copyStyleSheets();
      renderReactComponent();
    },
    [
      companionData,
      isMuted,
      muteMicrophone,
      userMicrophoneStream,
      startScreenShare,
      stopScreenShare,
      onStopTalking,
      sessionId,
      hideScreenShareButton,
      avatarId,
      organizationId,
      socketId,
    ],
  );

  const enterPiP = useCallback(async (): Promise<void> => {
    // Check if window is actually closed even if state says it's active
    if (pipWindowRef.current && pipWindowRef.current.closed) {
      pipWindowRef.current = null;
      setIsPiPActive(false);
    }

    if (!isPiPSupported) {
      throw new Error('Cannot enter PiP: not supported');
    }

    if (isPiPActive && pipWindowRef.current && !pipWindowRef.current.closed) {
      throw new Error('Cannot enter PiP: already active');
    }

    const pipWindow = await window.documentPictureInPicture?.requestWindow({
      width: 400,
      height: 300,
      disallowReturnToOpener: false,
    });

    if (!pipWindow) {
      throw new Error('Failed to create PiP window');
    }

    pipWindowRef.current = pipWindow;

    // Create PiP window content using DOM API
    createPiPContent(pipWindow, videoElement);

    // Clone video stream to PiP window
    if (videoElement && videoElement.srcObject) {
      const pipVideo = pipWindow.document.getElementById(
        'pipVideo',
      ) as HTMLVideoElement;
      if (pipVideo) {
        pipVideo.srcObject = videoElement.srcObject;
      }
    }

    // Handle PiP window close
    pipWindow.addEventListener('beforeunload', () => {
      // Collect final state before closing
      const finalState = getCurrentState();
      pipWindowRef.current = null;
      setIsPiPActive(false);
      applyStateChanges(finalState);
      onPiPExit?.(finalState);
    });

    setIsPiPActive(true);
    onPiPEnter?.();
  }, [
    isPiPSupported,
    isPiPActive,
    videoElement,
    createPiPContent,
    getCurrentState,
    applyStateChanges,
    onPiPEnter,
    onPiPExit,
    onEndSessionComplete,
    sendMessageToPiP,
  ]);

  const exitPiP = useCallback((): void => {
    if (!isPiPActive || !pipWindowRef.current) {
      throw new Error('Cannot exit PiP: not currently active');
    }

    try {
      // Collect final state before closing
      const finalState = getCurrentState();

      // Unmount React component asynchronously
      if (reactRootRef.current) {
        const root = reactRootRef.current;
        reactRootRef.current = null;

        // Defer unmounting to avoid race condition
        setTimeout(() => {
          root.unmount();
        }, 0);
      }

      pipWindowRef.current.close();
      pipWindowRef.current = null;
      setIsPiPActive(false);
      applyStateChanges(finalState);
      onPiPExit?.(finalState);
    } catch (error) {
      console.log('error', error);
      throw error;
    }
  }, [isPiPActive, getCurrentState, applyStateChanges, onPiPExit]);

  const togglePiP = useCallback(async (): Promise<void> => {
    if (isPiPActive) {
      exitPiP();
    } else {
      await enterPiP();
    }
  }, [isPiPActive, enterPiP, exitPiP]);

  // Register Media Session API handler for automatic PiP
  useEffect(() => {
    if (!isPiPSupported || !isSessionActive || !hasMediaStream) return;

    const handleEnterPiP = async () => {
      // Double-check state before attempting PiP
      if (pipWindowRef.current && pipWindowRef.current.closed) {
        pipWindowRef.current = null;
        setIsPiPActive(false);
      }

      try {
        await enterPiP();
      } catch (error) {
        console.log('error', error);
      }
    };

    try {
      if (
        'mediaSession' in navigator &&
        'setActionHandler' in navigator.mediaSession
      ) {
        // @ts-expect-error - 'enterpictureinpicture' is not yet in the standard MediaSessionAction type
        navigator.mediaSession.setActionHandler('enterpictureinpicture', () => {
          handleEnterPiP().catch(console.error);
        });
      }
    } catch (error) {
      console.log('Media Session API not supported:', error);
    }

    return () => {
      try {
        if (
          'mediaSession' in navigator &&
          'setActionHandler' in navigator.mediaSession
        ) {
          // @ts-expect-error - 'enterpictureinpicture' is not yet in the standard MediaSessionAction type
          navigator.mediaSession.setActionHandler('enterpictureinpicture', null);
        }
      } catch (error) {
        console.log('Media Session API not supported:', error);
      }
    };
  }, [isPiPSupported, isSessionActive, hasMediaStream, enterPiP]);

  // Additional visibility change handler to clean up state
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only clean up if the PiP window is actually closed
      if (
        !document.hidden &&
        pipWindowRef.current &&
        pipWindowRef.current.closed
      ) {
        pipWindowRef.current = null;
        setIsPiPActive(false);
      }
    };

    // Handle focus changes to preserve PiP window
    const handleFocus = () => {
      // Check if PiP window is still open when we regain focus
      if (pipWindowRef.current && pipWindowRef.current.closed) {
        pipWindowRef.current = null;
        setIsPiPActive(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Unmount React component asynchronously to avoid race condition
      if (reactRootRef.current) {
        const root = reactRootRef.current;
        reactRootRef.current = null;

        // Use setTimeout to defer unmounting until after current render
        setTimeout(() => {
          root.unmount();
        }, 0);
      }

      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        pipWindowRef.current.close();
        pipWindowRef.current = null;
      }
    };
  }, []);

  return {
    isPiPActive,
    isPiPSupported,
    enterPiP,
    exitPiP,
    togglePiP,
    sendMessageToPiP,
  };
};
